// ============================================================================
// ITEM CATEGORY MASTER (ADR-0058 slice 1) — the enterprise-wide category tree
// for items (ADR-0028 enterprise-shared). Ledger overrides per entity are kept
// in Inventory Settings, not here. The list is server-ordered by path, so the
// grid reads as a tree ("Non-Foods / Household").
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn,
  SyutSelect, type SelectOption, ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useItemCategories } from 'hooks/api';
import { itemCategoryService } from 'api/services';
import type { ItemCategoryDto, ItemCategoryQueryParams, CreateItemCategoryRequest } from 'api/types';

type ViewMode = 'list' | 'grid';

const ICONS = IconSystem as unknown as Record<string, React.ComponentType<{ size?: number }>>;

interface ApiError { response?: { status?: number; data?: { message?: string } } }
const apiStatus = (err: unknown) => (err as ApiError | null)?.response?.status;
const apiMessage = (err: unknown, fallback: string) =>
  (err as ApiError | null)?.response?.data?.message || fallback;
const CONCURRENCY_MESSAGE =
  'This category was changed by someone else since you opened it. Close the form and reopen it to get the latest version.';

interface FormData {
  categoryCode: string;
  categoryName: string;
  parentId: string;
  displayOrder: string;
  description: string;
  statusCode: string;
}

const EMPTY: FormData = {
  categoryCode: '', categoryName: '', parentId: '', displayOrder: '100', description: '', statusCode: 'ACTIVE',
};

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const categoryLabel = (c: ItemCategoryDto) => c.path || c.categoryName;

// ---------------------------------------------------------------------------
// FORM MODAL
// ---------------------------------------------------------------------------

interface CategoryFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record: ItemCategoryDto | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const { minimize } = useModalMinimize();
  const [form, setForm] = useState<FormData>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const parentLabelCache = useRef<Map<string, string>>(new Map());

  const formRef = useRef(form); formRef.current = form;
  const modeRef = useRef(mode); modeRef.current = mode;
  const recordRef = useRef(record); recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const r = recordRef.current;
    const m = modeRef.current;
    const data: FormData = r && m !== 'create'
      ? {
          categoryCode: m === 'copy' ? '' : r.categoryCode,
          categoryName: m === 'copy' ? `${r.categoryName} (Copy)` : r.categoryName,
          parentId: r.parentId || '',
          displayOrder: String(r.displayOrder ?? 100),
          description: r.description || '',
          statusCode: r.statusCode || 'ACTIVE',
        }
      : { ...EMPTY };
    if (r?.parentId && r.parentName) parentLabelCache.current.set(r.parentId, r.parentName);
    setForm(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(form) !== initialSnapshotRef.current;
  }, [form]);

  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(prev => {
      if (!prev[f]) return prev;
      const next = { ...prev }; delete next[f]; return next;
    });
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentMode = modeRef.current;
    const currentInstanceId = createInstanceIdRef.current || '1';
    const currentCode = recordRef.current?.categoryCode;
    const savedForm = { ...formRef.current };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedForm) !== savedSnapshot : false;

    const modalId = currentMode === 'create'
      ? `${tabId}-form-modal-create-${currentInstanceId}`
      : `${tabId}-form-modal-${currentMode}-${currentCode}`;

    const title = currentMode === 'create'
      ? (savedForm.categoryCode ? `Add - ${savedForm.categoryCode} (${currentInstanceId})` : `Add New (${currentInstanceId})`)
      : currentMode === 'copy'
        ? `Copy - ${currentCode}`
        : `Edit - ${currentCode}`;

    minimize({
      id: modalId, title, moduleName, tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(savedForm);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? currentInstanceId : undefined,
          currentMode !== 'create' ? currentCode : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const loadParents = useCallback(async (input: string): Promise<SelectOption[]> => {
    try {
      const res = await itemCategoryService.getAll({ searchTerm: input || undefined, pageSize: 50 });
      const excludeId = modeRef.current === 'edit' ? recordRef.current?.id : undefined;
      res.data.forEach(c => parentLabelCache.current.set(c.id, categoryLabel(c)));
      return res.data
        .filter(c => c.id !== excludeId)
        .map(c => ({ value: c.id, label: categoryLabel(c) }));
    } catch {
      return [];
    }
  }, []);

  const checkCode = useCallback(async (code: string): Promise<boolean> => {
    if (!code) return true;
    try {
      const excludeId = modeRef.current === 'edit' ? recordRef.current?.id : undefined;
      const ok = await itemCategoryService.isCodeAvailable(code, excludeId);
      if (!ok) setFieldErrors(p => ({ ...p, categoryCode: 'This category code is already in use.' }));
      return ok;
    } catch {
      return true; // the server enforces uniqueness on save
    }
  }, []);

  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!form.categoryCode.trim()) errors.categoryCode = 'Category code is required';
    if (!form.categoryName.trim()) errors.categoryName = 'Category name is required';
    const order = Number(form.displayOrder);
    if (form.displayOrder.trim() === '' || !Number.isInteger(order) || order < 0) {
      errors.displayOrder = 'Enter a whole number (0 or more)';
    }
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    if (!(await checkCode(form.categoryCode.trim()))) return;
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form, checkCode]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      const payload: CreateItemCategoryRequest = {
        categoryCode: form.categoryCode.trim(),
        categoryName: form.categoryName.trim(),
        parentId: form.parentId || null,
        displayOrder: Number(form.displayOrder),
        description: form.description.trim() || null,
        statusCode: form.statusCode,
      };
      if (mode === 'edit' && record) {
        await itemCategoryService.update(record.id, { ...payload, rowVersion: record.rowVersion });
      } else {
        await itemCategoryService.create(payload);
      }
      setSaving(false);
      notificationService.toast.success(
        `Item category "${payload.categoryName}" ${mode === 'edit' ? 'updated' : 'created'}.`,
      );
      onSaved();
      onClose();
    } catch (err: unknown) {
      setSaving(false);
      if (apiStatus(err) === 409) {
        notificationService.toast.error(apiMessage(err, CONCURRENCY_MESSAGE));
        onSaved();
      } else {
        notificationService.toast.error(apiMessage(err, 'Could not save the item category.'));
      }
    }
  }, [form, mode, record, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {saving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {mode === 'create' ? 'Add' : mode === 'copy' ? 'Copy' : 'Edit'} Item Category
          </h2>
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="categoryCode">
              <label className="form-label">Category Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('categoryCode') ? ' form-field-error' : ''}`}
                value={form.categoryCode}
                maxLength={30}
                placeholder="e.g. NONFOOD-HH"
                onChange={e => { clearFieldError('categoryCode'); setForm(p => ({ ...p, categoryCode: e.target.value.toUpperCase() })); }}
                onBlur={e => { void checkCode(e.target.value.trim()); }}
              />
              {getFieldError('categoryCode') && <div className="form-error-message">{getFieldError('categoryCode')}</div>}
            </div>

            <div className="form-group" data-field="categoryName">
              <label className="form-label">Category Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('categoryName') ? ' form-field-error' : ''}`}
                value={form.categoryName}
                maxLength={150}
                placeholder="Enter category name"
                onChange={e => { clearFieldError('categoryName'); setForm(p => ({ ...p, categoryName: e.target.value })); }}
              />
              {getFieldError('categoryName') && <div className="form-error-message">{getFieldError('categoryName')}</div>}
            </div>

            <div className="form-group" data-field="parentId">
              <SyutSelect
                label="Parent Category"
                isAsync loadOptions={loadParents} defaultOptions cacheOptions={false}
                seedOptions={form.parentId
                  ? [{ value: form.parentId, label: parentLabelCache.current.get(form.parentId) || '' }]
                  : undefined}
                menuPortalTarget={document.body}
                value={form.parentId}
                onChange={v => setForm(p => ({ ...p, parentId: (v as string) || '' }))}
                placeholder="None (top level)"
                isClearable
              />
            </div>

            <div className="form-group" data-field="displayOrder">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                min={0}
                step={1}
                className={`form-input${getFieldError('displayOrder') ? ' form-field-error' : ''}`}
                value={form.displayOrder}
                onChange={e => { clearFieldError('displayOrder'); setForm(p => ({ ...p, displayOrder: e.target.value })); }}
              />
              {getFieldError('displayOrder') && <div className="form-error-message">{getFieldError('displayOrder')}</div>}
            </div>
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              options={STATUS_OPTIONS}
              value={form.statusCode}
              onChange={val => setForm(p => ({ ...p, statusCode: (val as string) || 'ACTIVE' }))}
              isClearable={false}
              menuPortalTarget={document.body}
            />
          </div>

          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={form.description}
              placeholder="What belongs in this category"
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => { void handleSave(); }} disabled={saving}>
            <IconSystem.check size={16} /> {mode === 'edit' ? 'Save' : 'Create'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`${mode === 'edit' ? 'Save changes to' : 'Create'} item category "${form.categoryName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// VIEW MODAL
// ---------------------------------------------------------------------------

const ViewField: React.FC<{ label: string; value?: string | number | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{
      padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem',
      border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px', whiteSpace: 'pre-wrap',
    }}>
      {value !== null && value !== undefined && value !== '' ? value : <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

interface CategoryViewModalProps {
  isOpen: boolean;
  record: ItemCategoryDto | null;
  onClose: () => void;
  onEdit?: (r: ItemCategoryDto) => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (code: string) => void;
}

const CategoryViewModal: React.FC<CategoryViewModalProps> = ({
  isOpen, record, onClose, onEdit, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !record) return;
    minimize({
      id: `${tabId}-view-modal-${record.categoryCode}`,
      title: `View - ${record.categoryCode}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(record.categoryCode),
    });
    onClose();
  }, [tabId, moduleName, record, minimize, onClose, onRestoreView]);

  if (!isOpen || !record) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Item Category Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Category Code" value={record.categoryCode} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px' }}>
                <span className="badge" style={record.statusColor ? { background: record.statusColor, color: '#fff' } : undefined}>
                  {record.statusName || record.statusCode || 'Active'}
                </span>
              </div>
            </div>
            <ViewField label="Category Name" value={record.categoryName} fullWidth />
            <ViewField label="Path" value={record.path} fullWidth />
            <ViewField label="Parent Category" value={record.parentName || 'None (top level)'} />
            <ViewField label="Display Order" value={record.displayOrder} />
            <ViewField label="Items" value={record.itemCount ?? 0} />
            <ViewField label="Sub-categories" value={record.childCount ?? 0} />
            <ViewField label="Ledger Overrides" value={record.ledgerOverrideCount ?? 0} />
            <div />
            <ViewField label="Description" value={record.description} fullWidth />
            <ViewField label="Created By" value={record.createdByName} />
            <ViewField label="Created At" value={record.createdAt ? new Date(record.createdAt).toLocaleString() : null} />
            {record.updatedAt && (
              <>
                <ViewField label="Updated By" value={record.updatedByName} />
                <ViewField label="Updated At" value={new Date(record.updatedAt).toLocaleString()} />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(record); }}>
              <IconSystem.edit size={16} /> Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// SCREEN
// ---------------------------------------------------------------------------

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'categoryCode', label: 'Code' },
  { id: 'categoryName', label: 'Name' },
  { id: 'path', label: 'Path' },
  { id: 'itemCount', label: 'Items' },
  { id: 'childCount', label: 'Sub-categories' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface ItemCategoryScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const ItemCategoryScreen: React.FC<ItemCategoryScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const can = useCan('item-category');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  const [queryParams, setQueryParams] = useState<ItemCategoryQueryParams>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useItemCategories(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('item-category-columns', COLUMN_DEFS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [rootsOnly, setRootsOnly] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selected, setSelected] = useState<ItemCategoryDto | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState<string>('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<ItemCategoryDto | null>(null);

  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormMode('create'); setSelected(null); setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleEdit = useCallback((r: ItemCategoryDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${r.categoryCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    setFormMode('edit'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleCopy = useCallback((r: ItemCategoryDto) => {
    const modalId = tabId ? `${tabId}-form-modal-copy-${r.categoryCode}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId); modal?.onRestore?.();
      return;
    }
    if (formOpen) formModalMinimizeRef.current?.();
    setFormMode('copy'); setSelected(r); setFormOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal, formOpen]);

  const handleView = useCallback((r: ItemCategoryDto) => {
    const viewId = tabId ? `${tabId}-view-modal-${r.categoryCode}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId); modal?.onRestore?.();
      return;
    }
    setViewRecord(r); setViewOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((code: string) => {
    const rec = data.find(r => r.categoryCode === code);
    if (rec) { setViewRecord(rec); setViewOpen(true); }
  }, [data]);

  const handleRestoreModal = useCallback(
    (m: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => {
      if (m === 'create' && instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(r => r.categoryCode === recordCode);
        if (rec) setSelected(rec);
      }
      setFormMode(m); setFormOpen(true);
    },
    [data],
  );

  const handleDelete = useCallback(async (r: ItemCategoryDto) => {
    const blockers: string[] = [];
    if ((r.childCount ?? 0) > 0) blockers.push(`${r.childCount} sub-categor${r.childCount === 1 ? 'y' : 'ies'}`);
    if ((r.itemCount ?? 0) > 0) blockers.push(`${r.itemCount} item${r.itemCount === 1 ? '' : 's'}`);
    if ((r.ledgerOverrideCount ?? 0) > 0) blockers.push(`${r.ledgerOverrideCount} ledger override${r.ledgerOverrideCount === 1 ? '' : 's'}`);
    const ok = await confirmDelete({
      resourceLabel: r.categoryName,
      message: blockers.length
        ? `"${r.categoryName}" still has ${blockers.join(', ')}. The server will refuse the delete until they are moved or removed. Try anyway?`
        : undefined,
    });
    if (!ok) return;
    try {
      await itemCategoryService.delete(r.id, r.rowVersion);
      notificationService.toast.success(`Item category "${r.categoryName}" deleted.`);
      refetch();
    } catch (err: unknown) {
      // The API refuses while sub-categories, items or ledger overrides remain — surface its reason.
      notificationService.toast.error(apiMessage(err, 'Could not delete the item category.'));
      if (apiStatus(err) === 409) refetch();
    }
  }, [confirmDelete, refetch]);

  const columns: SyutGridColumn<ItemCategoryDto>[] = useMemo(() => [
    { id: 'categoryCode', header: 'Code', accessorKey: 'categoryCode', size: 140 },
    { id: 'categoryName', header: 'Name', accessorKey: 'categoryName', size: 220 },
    {
      id: 'path', header: 'Path', accessorKey: 'path', size: 300,
      cell: ({ row }) => (row.original as ItemCategoryDto).path || (row.original as ItemCategoryDto).categoryName,
    },
    {
      id: 'itemCount', header: 'Items', accessorKey: 'itemCount', size: 90, align: 'right',
      cell: ({ row }) => (row.original as ItemCategoryDto).itemCount ?? 0,
    },
    {
      id: 'childCount', header: 'Sub-categories', accessorKey: 'childCount', size: 130, align: 'right',
      cell: ({ row }) => (row.original as ItemCategoryDto).childCount ?? 0,
    },
    {
      id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110,
      cell: ({ row }) => {
        const r = row.original as ItemCategoryDto;
        return (
          <span className="badge" style={r.statusColor ? { background: r.statusColor, color: '#fff' } : undefined}>
            {r.statusName || 'Active'}
          </span>
        );
      },
    },
  ], []);

  const rowActions = useCallback((r: ItemCategoryDto): ActionMenuItem[] => {
    // Order: View → Copy → Edit → Delete. View is never gated.
    const items: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(r) },
    ];
    if (can('CREATE')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(r) });
    }
    if (can('UPDATE')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(r) });
    }
    if (can('DELETE')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => { void handleDelete(r); }, variant: 'danger' });
    }
    return items;
  }, [can, handleView, handleEdit, handleCopy, handleDelete]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Add Category'), visible: can('CREATE') },
    actionButtonFactory.refresh(refetch),
  ];

  const appliedCount = (rootsOnly ? 1 : 0) + (includeInactive ? 1 : 0);
  const HeaderIcon = moduleIcon ? ICONS[moduleIcon] : undefined;

  return (
    <EnterpriseScreenShell
      title="Item Categories"
      description="Enterprise-wide category tree for items; categories carry ledger overrides per entity in Inventory Settings"
      icon={HeaderIcon ? <HeaderIcon size={18} /> : <IconSystem.folder size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search categories...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{
        onClick: () => setFilterOpen(!filterOpen),
        isActive: filterOpen,
        appliedCount,
      }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={rootsOnly}
                    onChange={e => setRootsOnly(e.target.checked)} /> Top level only
                </label>
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeInactive}
                    onChange={e => setIncludeInactive(e.target.checked)} /> Include inactive
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setRootsOnly(false); setIncludeInactive(false);
                setQueryParams(p => ({ page: 1, pageSize: p.pageSize, searchTerm: p.searchTerm }));
              }}>Clear</button>
              <button className="btn btn-primary" onClick={() => {
                setQueryParams(p => ({
                  ...p,
                  rootsOnly: rootsOnly || undefined,
                  includeInactive: includeInactive || undefined,
                  page: 1,
                }));
                setFilterOpen(false);
              }}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No item categories yet — click Add.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{r.categoryCode}</div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.categoryName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{r.path || r.categoryName}</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {r.itemCount ?? 0} item{(r.itemCount ?? 0) === 1 ? '' : 's'} · {r.childCount ?? 0} sub-categor{(r.childCount ?? 0) === 1 ? 'y' : 'ies'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <CategoryFormModal isOpen={formOpen} mode={formMode} record={selected}
        onSaved={refetch}
        onClose={() => { setFormOpen(false); setSelected(null); }}
        tabId={tabId} moduleName={moduleName || 'Item Categories'}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />

      <CategoryViewModal isOpen={viewOpen} record={viewRecord}
        onClose={() => { setViewOpen(false); setViewRecord(null); }}
        onEdit={can('UPDATE') ? handleEdit : undefined}
        tabId={tabId} moduleName={moduleName || 'Item Categories'}
        onRestoreView={handleRestoreView} />
    </EnterpriseScreenShell>
  );
};

export default ItemCategoryScreen;
